import toastr from 'react-hot-toast';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import { CACHE_AND_STALE_TIME } from 'constants/index';
import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import {
  generateFilter,
  generateIncludeQuery,
  generateSortQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import type {
  AIModelExecutionParameterResponse,
  JoinedAiModelParameter,
} from 'types/AIModelExecutionParameters';
import React from 'react';
import { Parameter } from 'types/Parameters';
import { numericSort } from 'utilities/sort';

const getExecutionParameters = (aiModelID: string | null | undefined) => {
  const queryArray = [
    generateSortQuery('sortOrder', 'Acs'),
    generateIncludeQuery(['parameter']),
  ];
  if (aiModelID) {
    queryArray.push(generateFilter('aiModelID', aiModelID));
  }
  const url = generateURL(
    SPARK_RESOURCE_TYPES.AI_MODEL_EXECUTION_PARAMETERS,
    queryArray
  );
  return fetchWrapper<AIModelExecutionParameterResponse>({
    url: url,
    method: 'GET',
  });
};

export function useAIModelExecutionParameters(
  aiModelID?: string | null | undefined
) {
  const queryResult = useQuery(
    ['AIModelExecutionParameters', aiModelID],
    () => getExecutionParameters(aiModelID),
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(
            `Failed to get AI model execution parameters: ${error.message}`
          );
        }
      },
      ...CACHE_AND_STALE_TIME,
      enabled: Boolean(aiModelID),
    }
  );
  const { data } = queryResult;

  const { data: aIModelExecutionParameter, included } = data || {};

  const parameterIdMap = React.useMemo(
    () =>
      included?.reduce<Record<string, Parameter>>((acc, curr) => {
        if (curr.type === SPARK_RESOURCE_TYPES.PARAMETER) {
          return { ...acc, [curr.id]: curr as Parameter };
        }
        return acc;
      }, {}),
    [included]
  );

  const joinedAiModelParameter: JoinedAiModelParameter[] | undefined =
    React.useMemo(() => {
      const joinedParameter = aIModelExecutionParameter
        ?.map<JoinedAiModelParameter | undefined>((executionParameter) => {
          const executionParameterAttributes = executionParameter.attributes;
          const parameterID = executionParameterAttributes.parameterID;
          const parameter = parameterIdMap?.[parameterID];
          if (!parameter) {
            return undefined;
          }
          return {
            aiModelExecutionParameterID: executionParameter.id,
            id: parameterID,
            aiModelID: executionParameterAttributes.aiModelID,
            sortOrder: executionParameterAttributes.sortOrder,
            parameterAttributes: parameter?.attributes,
          };
        })
        .filter(Boolean) as JoinedAiModelParameter[];
      return joinedParameter?.sort((a, b) =>
        numericSort(a.sortOrder, b.sortOrder)
      );
    }, [aIModelExecutionParameter, parameterIdMap]);

  const uniqueParameterNameMap = joinedAiModelParameter?.reduce<
    Record<string, JoinedAiModelParameter>
  >((acc, parameter) => {
    return {
      ...acc,
      [`${parameter.id}-${parameter.aiModelExecutionParameterID}`]: parameter,
    };
  }, {});

  return {
    joinedAiModelParameter,
    aIModelExecutionParameter,
    uniqueParameterNameMap,
    ...queryResult,
  };
}
